import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { researchCms } from "@/api/researchCms.service";
import { CmsConfirmDialog } from "../../../../../components/ResearchCms/CmsConfirmDialog/CmsConfirmDialog";
import { archiveBlockedInSplitOrMergeReason } from "../../utils/bpaArchive";
import type { ImportDoneState } from "./importDone";
import { archiveConfirmMessage, describeCounts } from "./importText";
import pageStyles from "../../CmsBpaPage.module.css";
import styles from "./ImportBanners.module.css";

type ImportDoneBannerProps = {
  done: ImportDoneState;
  canArchive: boolean;
  canDelete: boolean;
  onDismiss: () => void;
};

const ARCHIVE_REASON_ID = "import-done-archive-reason";

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "The practice area could not be archived.";
}

/** Shown on the target BPA page after an import: what came across, and the offer to archive the source. */
export function ImportDoneBanner({
  done,
  canArchive,
  canDelete,
  onDismiss,
}: ImportDoneBannerProps) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const archive = useMutation({
    mutationFn: () => researchCms.archiveEditionBpa(done.sourceEditionBpaId),
    onSuccess: () => {
      setConfirming(false);
      queryClient.invalidateQueries({ queryKey: ["research-edition-bpas"] });
      queryClient.invalidateQueries({ queryKey: ["bpa-import-target"] });
    },
    onError: () => setConfirming(false),
  });

  const sourceQuery = useQuery({
    queryKey: ["research-edition-bpa", done.sourceEditionBpaId],
    queryFn: () => researchCms.getEditionBpa(done.sourceEditionBpaId),
  });
  const archiveBlockedReason = archiveBlockedInSplitOrMergeReason({
    canDelete,
    inSplitOrMerge: sourceQuery.data?.editionBpa.inSplitOrMerge,
  });

  const organisations = `${done.organisations} ${done.organisations === 1 ? "organisation" : "organisations"}`;
  const scores = done.withScores ? ", with their SARs scores" : "";

  return (
    <div className={`${styles.banner} ${styles.done}`} role="status" data-bpa-banner="">
      <div className={styles.text}>
        <strong>
          Imported {organisations} from {done.sourceBpaName}
        </strong>
        <span>
          {describeCounts(done.imported)}
          {scores}.
        </span>
      </div>
      <div className={styles.actions}>
        {archive.isSuccess ? (
          <span>{done.sourceBpaName} archived.</span>
        ) : (
          canArchive && (
            <>
              {archiveBlockedReason && (
                <span id={ARCHIVE_REASON_ID} className={styles.reason}>
                  {archiveBlockedReason}
                </span>
              )}
              <button
                type="button"
                className={pageStyles.editBpaLink}
                onClick={() => setConfirming(true)}
                disabled={archive.isPending || archiveBlockedReason !== null}
                aria-describedby={archiveBlockedReason ? ARCHIVE_REASON_ID : undefined}
              >
                Archive source
              </button>
            </>
          )
        )}
        <button type="button" className={styles.dismiss} onClick={onDismiss}>
          Dismiss
        </button>
      </div>
      {archive.isError && (
        <div className={styles.error} role="alert">
          {errorMessage(archive.error)}
        </div>
      )}
      {confirming && (
        <CmsConfirmDialog
          title="Archive practice area"
          message={archiveConfirmMessage(done.sourceBpaName)}
          tone="warning"
          confirmLabel={archive.isPending ? "Archiving..." : "Archive"}
          cancelLabel="Cancel"
          onCancel={() => !archive.isPending && setConfirming(false)}
          onConfirm={() => archive.mutate()}
        />
      )}
    </div>
  );
}
